import * as React from 'react';
import type { SVGProps } from 'react';
import OtIcon from './OtIcon';

/**
 * Illustrated family — ambient group: the time of day in the header, and the settings entry.
 *
 * The sun and the moon are the reason this family exists. The header greeting used to carry
 * `☀`/`🌙` emoji; they were reduced to grey glyphs and the header lost the one element that was not
 * data — the regression BR-UI-36 in `docs/guidelines/frontend.md` was written about. Drawn as icons
 * they stay controllable: same lighting model as the rest, and identical on every platform, which an
 * emoji never is.
 */

type TProps = SVGProps<SVGSVGElement>;

const Spec = ({ d, o = 0.92, w = 2.6 }: { d: string; o?: number; w?: number }) => (
	<path d={d} stroke='#fff' strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

const Bounce = ({ d, c, o = 0.5, w = 2 }: { d: string; c: string; o?: number; w?: number }) => (
	<path d={d} stroke={c} strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

/** Ray endpoints, so the rays share one gradient axis with the disc. */
const RAYS = Array.from({ length: 8 }, (_, i) => {
	const a = (i * Math.PI) / 4;
	return {
		x1: (46 + Math.cos(a) * 27).toFixed(1),
		y1: (42 + Math.sin(a) * 27).toFixed(1),
		x2: (46 + Math.cos(a) * 35).toFixed(1),
		y2: (42 + Math.sin(a) * 35).toFixed(1),
	};
});

/* ------------------------------------------------------------------ Sun */
/**
 * The sun in the greeting.
 *
 * This one is not a menu glyph and is not built like one. It is the first thing a person sees when
 * they open the product, and its job is to feel like light arriving: a white-hot core, a warm body,
 * a corona that bleeds past the disc, and rays of uneven length so it reads as burning rather than
 * as a wheel. It also fills the whole 96 grid instead of sitting inside the family's usual margins
 * — there is nothing else on that row for it to line up with.
 *
 * No ground shadow either: light does not sit on a floor. `groundR={0}` collapses the contact
 * shadow the base would otherwise draw.
 */
export const OtSun = (props: TProps) => (
	<OtIcon
		slug='sun'
		palette='gold'
		groundX={48}
		groundY={96}
		groundR={0}
		extraDefs={(refs, id) => (
			<>
				{/* the bloom that spills past the edge — this is what reads as warmth */}
				<radialGradient id={id('bloom')} cx='.5' cy='.5' r='.5'>
					<stop offset='0' stopColor='#ffd166' stopOpacity='.75' />
					<stop offset='.45' stopColor='#ffb62e' stopOpacity='.38' />
					<stop offset='1' stopColor='#ff8a3d' stopOpacity='0' />
				</radialGradient>
				{/* the body: white-hot at the top-left, deep gold at the far edge */}
				<radialGradient id={id('body')} gradientUnits='userSpaceOnUse' cx='40' cy='36' r='34'>
					<stop offset='0' stopColor='#fffdf2' />
					<stop offset='.32' stopColor='#ffe07a' />
					<stop offset='.72' stopColor='#ffb62e' />
					<stop offset='1' stopColor='#f08a12' />
				</radialGradient>
				<linearGradient id={id('ray')} gradientUnits='userSpaceOnUse' x1='20' y1='16' x2='76' y2='80'>
					<stop offset='0' stopColor='#fff3c4' />
					<stop offset='.5' stopColor='#ffc24a' />
					<stop offset='1' stopColor='#ff8a3d' />
				</linearGradient>
			</>
		)}
		{...props}>
		{(r) => (
			<>
				<circle cx='48' cy='48' r='48' fill={r.own('bloom')} />
				{/* twelve rays, alternating long and short: an even ring reads as a gear */}
				{Array.from({ length: 12 }, (_, i) => {
					const a = (i * Math.PI) / 6;
					const inner = 27;
					const outer = i % 2 === 0 ? 44 : 37;
					return (
						<path
							key={a}
							d={`M${(48 + Math.cos(a) * inner).toFixed(1)} ${(48 + Math.sin(a) * inner).toFixed(1)}L${(48 + Math.cos(a) * outer).toFixed(1)} ${(48 + Math.sin(a) * outer).toFixed(1)}`}
							stroke={r.own('ray')}
							strokeWidth={i % 2 === 0 ? 5.4 : 4.2}
							strokeLinecap='round'
						/>
					);
				})}
				<circle cx='48' cy='48' r='24' fill={r.own('body')} />
				{/* the corona: a bright edge all the way round, because a sun has no shadowed side */}
				<circle
					cx='48'
					cy='48'
					r='24'
					fill='none'
					stroke='#fff3c4'
					strokeOpacity='.85'
					strokeWidth='2.4'
				/>
				{/* the hot spot, and the highlight that sells the sphere */}
				<circle cx='40' cy='39' r='9' fill='#fffdf2' fillOpacity='.55' />
				<path
					d='M33 41a16 16 0 0 1 9-9'
					fill='none'
					stroke='#fffdf2'
					strokeWidth='3.2'
					strokeLinecap='round'
				/>
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Moon */
export const OtMoon = (props: TProps) => (
	<OtIcon slug='moon' palette='indigo' groundX={46} groundY={83} groundR={16} {...props}>
		{(r) => (
			<>
				<path d='M56 16a30 30 0 1 0 20 47 24 24 0 0 1-20-47z' fill={r.fill} />
				<path
					d='M56 16a30 30 0 1 0 20 47 24 24 0 0 1-20-47z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
				/>
				{/* craters: a rim of shadow and a lit floor, so they read as dents and not as dots */}
				<ellipse cx='38' cy='38' rx='4.6' ry='4.2' fill='#231d4e' opacity='.42' />
				<ellipse cx='37.2' cy='37.2' rx='3.4' ry='3' fill='#b7abf5' opacity='.3' />
				<circle cx='32' cy='56' r='2.9' fill='#231d4e' opacity='.34' />
				<Spec d='M31 24a27 27 0 0 1 11-6.6' o={0.95} w={2.7} />
				<Bounce d='M70 62a25 25 0 0 1-9 5.4' c='#7fe6d2' />
				<path
					d='M80 26l1.6 3.9 3.9 1.6-3.9 1.6L80 37l-1.6-3.9-3.9-1.6 3.9-1.6z'
					fill='#7fe6d2'
					filter={r.glow}
					opacity='.85'
				/>
				<path d='M80 26l1.6 3.9 3.9 1.6-3.9 1.6L80 37l-1.6-3.9-3.9-1.6 3.9-1.6z' fill='#eafff9' />
				<path
					d='M17 34l1.2 3 3 1.2-3 1.2L17 42.6l-1.2-3-3-1.2 3-1.2z'
					fill='#e6ddff'
					opacity='.9'
				/>
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Settings */
/**
 * Three sliders, and nothing behind them.
 *
 * The first version drew them inside a rounded panel. At 24px that panel reads as a box someone
 * drew around the icon rather than as part of it — the illustrated family already gives every icon
 * a body, a light and a shadow, so a container behind it is one object too many. The sliders are
 * the object.
 */
export const OtSettings = (props: TProps) => (
	<OtIcon slug='settings' palette='indigo' groundX={46} groundY={80} groundR={26} {...props}>
		{(r) => (
			<>
				{[
					{ y: 20, len: 72, on: 44, knob: 56 },
					{ y: 40, len: 52, on: 22, knob: 34 },
					{ y: 60, len: 64, on: 54, knob: 66 },
				].map((s) => (
					<g key={s.y}>
						{/* the track is cut into the surface, so it is darker than what fills it */}
						<rect
							x='10'
							y={s.y}
							width={s.len}
							height='11'
							rx='5.5'
							fill={r.tone.occlusion}
							fillOpacity='.45'
						/>
						<rect x='10' y={s.y} width={s.on} height='11' rx='5.5' fill={r.fill} />
						<rect
							x='10'
							y={s.y}
							width={s.len}
							height='11'
							rx='5.5'
							fill='none'
							stroke={r.rim}
							strokeWidth='2.4'
						/>
						<ellipse
							cx={s.knob}
							cy={s.y + 13}
							rx='9'
							ry='2.6'
							fill={r.tone.occlusion}
							opacity='.5'
							filter={r.tight}
						/>
						<circle cx={s.knob} cy={s.y + 5.5} r='9' fill={r.body} />
						<circle
							cx={s.knob}
							cy={s.y + 5.5}
							r='9'
							fill='none'
							stroke={r.rim}
							strokeWidth='2.4'
						/>
					</g>
				))}
				<Spec d='M14 22a5 5 0 0 1 5-3.6' o={0.85} w={2.2} />
				<Bounce d='M78 66a5 5 0 0 1-4 4' c={r.tone.bounce} o={0.55} />
			</>
		)}
	</OtIcon>
);
